import { useState, useEffect } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormLabel,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import * as React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { createTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { IGreenhouse } from "../../interfaces/IGreenhouse";
import { IBay } from "../../interfaces/IBay";
import { IZone } from "../../interfaces/IZone";
import { IBench } from "../../interfaces/IBench";
import { IItemHeader } from "../../interfaces/IItemHeader";
import { IItemDetail } from "../../interfaces/IItemDetail";
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { ITray } from "../../interfaces/ITray";
import { setPageTitle } from "../../slices/applicationSlice";
import { IApplication } from "../../interfaces/IApplication";
import { useAppSelector, useAppDispatch } from "../../hooks";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { DatePicker } from "@mui/x-date-pickers";


const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.grey[200],
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },
}));

const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 230 },
    { field: 'description', headerName: 'Description', width: 230 },
    { field: 'allocatedSpace', headerName: 'Allocated Space', width: 230 },
    { field: 'availableSpace', headerName: 'Available Space', width: 230 },
];

interface WorkLogRow {
    description: string,
    material: string,
    size: string,
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

export default function TrayImport() {

    const [openSuccess, setSuccess] = React.useState(false);

    const handleSuccess = () => {
        setSuccess(true);
    };

    const handleSuccessClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSuccess(false);
    };

    const dispatch = useAppDispatch();
    const userSelector = useAppSelector((state) => { return state.application.loggedInUser })

    useEffect(() => {
        let appState: IApplication = {
            pageTitle: "Tray Import",
            backgroundPath: "/assets/images/inventorybg.jpg"
        }
        dispatch(setPageTitle(appState));
    }, []);

    const [itemHeaders, setItemHeaders] = React.useState<IItemHeader[]>([]);
    const [itemDetails, setItemDetails] = React.useState<IItemDetail[]>([]);
    const [selectedItemDetail, setSelectedItemDetail] = React.useState<IItemDetail>();
    const [greenHouses, setGreenHouses] = React.useState<IGreenhouse[]>([]);
    const [currentGreenhouse, setCurrentGreenhouse] = React.useState<IGreenhouse>();
    const [bays, setBays] = React.useState<IBay[]>([]);
    const [currentBay, setCurrentBay] = React.useState<IBay>();
    const [zones, setZones] = React.useState<IZone[]>([]);
    const [currentZone, setCurrentZone] = React.useState<IZone>();
    const [benches, setBenches] = React.useState<IBench[]>([]);
    const [currentBench, setCurrentBench] = React.useState<IBench>();

    const [rows, setRows] = useState<IGreenhouse[] | IBay[] | IZone[] | IBench[] | ITray[]>([]);

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/ItemHeaders`)
            .then(res => {
                console.log("Response", res);
                setItemHeaders(res.data);
            });
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/ItemDetails`)
            .then(res => {
                console.log("Response", res);
                setItemDetails(res.data);
            });
        axios
            .get(`${process.env.REACT_APP_ENDPOINT}/api/Greenhouses`)
            .then(res => {
                console.log("Response", res);
                setGreenHouses(res.data);
            });
    }, []);

    useEffect(() => {
        if (currentGreenhouse) {
            setBays(currentGreenhouse.bays)
            setRows(currentGreenhouse.bays);
        } else {
            setBays([]);
            setRows([])
            setCurrentBay(undefined);
        }
    }, [currentGreenhouse])

    useEffect(() => {
        if (currentBay) {
            setZones(currentBay.zones)
            setRows(currentBay.zones);
        } else {
            setZones([]);
            if (currentGreenhouse && currentGreenhouse.bays) {
                setRows(currentGreenhouse.bays);
            }
            setCurrentZone(undefined);
        }
    }, [currentBay])

    useEffect(() => {
        if (currentZone) {
            setBenches(currentZone.benches)
            setRows(currentZone.benches);
        } else {
            setBenches([]);
            if (currentBay && currentBay.zones) {
                setRows(currentBay.zones);
            }
        }
    }, [currentZone])

    useEffect(() => {
        if (currentBench) {
            // setRows(currentBench.trays)
        } else {
            setBenches([]);
        }
    }, [currentBench])

    let history = useNavigate();
    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#191919' : '#fff',
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        backgroundImage: "none",

    }));


    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    interface IFormInput {
        ItemNumber: string
        Type: string
        Name: string
        Item: IItemHeader
        ItemDetail: IItemDetail,
        TraySize: number,
        Species: string,
        Variety: string,
        Color: string,
        Description: string,
        CropTime: number,
        IsTransferType: boolean,
        CustomerName: string,
        Quantity: number,
        Buffer: number,
        StartingBarcode: string,
        SowDate: string,
        SowWeek: number,
        ProductionDate: string,
        ProductionWeek: number,
        EnteredBy: string,
        AP: string,
        TransferTrayTime: number
    }

    const { register, handleSubmit, control, formState: { errors }, getValues, setValue, reset } = useForm<IFormInput>();
    const onSubmit: SubmitHandler<IFormInput> = data => {
        console.log(data);
        axios.post(`${process.env.REACT_APP_ENDPOINT}/api/ItemHeaders/tray-import`, {
            itemNumber: 'Testing',
            traySize: data.TraySize,
            species: data.Species,
            variety: data.Variety,
            color: data.Color,
            description: data.Description,
            cropTime: data.CropTime,
            isTransferType: data.IsTransferType,
            customerName: data.CustomerName,
            quantity: data.Quantity,
            startingBarCode: data.StartingBarcode,
            greenhouseId: currentGreenhouse?.id,
            bayId: currentBay?.id,
            zoneId: currentZone?.id,
            benchId: currentBench?.id
        }).then((data) => {
            console.log(data);
            if (data.status === 204) {

            };
        }).catch(x => { console.log(x.message) });
    };

    const setItem = (data: IItemDetail | null) => {
        console.log(data);
        var splitInfo = data?.description.split(" ").map(function (item) {
            return item.trim();
        });
        console.log("SPLIT INFO", splitInfo);
        let formData: IFormInput = {
            ItemNumber: '',
            Type: data?.type!,
            Name: data?.description!,
            Item: data?.itemHeader!,
            ItemDetail: data!,
            TraySize: data?.size!,
            Species: splitInfo != undefined && splitInfo[1] != undefined ? splitInfo[1] : "",
            Variety: splitInfo != undefined && splitInfo[2] != undefined ? splitInfo[2] : "",
            Color: splitInfo != undefined && splitInfo[3] != undefined ? splitInfo[3] : "",
            Description: data?.description != undefined ? data?.description : "",
            CropTime: 0,
            IsTransferType: false,
            CustomerName: "",
            Quantity: 50,
            Buffer: 10,
            StartingBarcode: "",
            SowDate: new Date().toDateString(),
            SowWeek: 0,
            ProductionDate: new Date().toDateString(),
            ProductionWeek: 0,
            EnteredBy: userSelector?.userName != undefined ? userSelector.userName : '',
            AP: "",
            TransferTrayTime: 0
        }
        reset(formData);
        SetSowWeek();
        SetProductionWeek();
    }

    const trayColumns: GridColDef[] = [
        //{ field: 'id', headerName: 'ID', width: 70 },
        { field: 'name', headerName: 'Name', width: 250 },
        { field: 'description', headerName: 'Description', width: 350 },
        { field: 'species', headerName: 'Species', width: 300 },
        { field: 'variety', headerName: 'Variety', width: 250 },
        { field: 'color', headerName: 'Color', width: 200 },
    ];

    const [trayRows, setTrayRows] = useState<ITray[]>([]);

    function SimulateTrays() {
        let trays: Array<ITray> = [];
        var quantityRequested = getValues("Quantity");
        for (let index = 0; index < quantityRequested; index++) {
            let tray: ITray = {
                id: 0,
                name: getValues("Name"),
                description: getValues("Description"),
                species: getValues("Species"),
                variety: getValues("Variety"),
                createdAt: new Date(),
                updatedAt: new Date()
            }

            trays.push(tray);
        }
        setTrayRows(trays);
    }

    function SetSowWeek() {
        var week = getValues('SowDate');
        var dateWeek = new Date(week);
        axios.get(`${process.env.REACT_APP_ENDPOINT}/api/WeekCalendars/weeks/${dateWeek.getMonth()}-${dateWeek.getDay()}-${dateWeek.getFullYear()}`, {
        }).then((data) => {
            console.log(data);
            if (data.status === 200) {
                setValue('SowWeek', data.data)
            };
        }).catch(x => { console.log(x.message) });
    }

    function SetProductionWeek() {
        var week = getValues('ProductionDate');
        var dateWeek = new Date(week);
        axios.get(`${process.env.REACT_APP_ENDPOINT}/api/WeekCalendars/weeks/${dateWeek.getMonth()}-${dateWeek.getDay()}-${dateWeek.getFullYear()}`, {
        }).then((data) => {
            console.log(data);
            if (data.status === 200) {
                setValue('ProductionWeek', data.data)
            };
        }).catch(x => { console.log(x.message) });
    }

    return (
        <Box id="mainForm" component="form">
            <Grid container spacing={0}>
                <Grid item xs={5} bgcolor='rgba(255, 255, 255, 0.85)'>
                    <Grid container spacing={0} sx={{ height: 'calc(100vh - 60px)' }} >
                        <Grid item xs={12}>
                            <Grid container spacing={1} padding={3}>
                                <Grid item xs={12}>
                                    <Item sx={{ bgcolor: 'transparent' }}>
                                        <Controller
                                            control={control}
                                            name="ItemDetail"
                                            render={({ field: { ref, onChange, ...field } }) => (
                                                <Autocomplete
                                                    options={itemDetails}
                                                    getOptionLabel={(option) => `${option.material} - ${option.description} - ${option.size}`}
                                                    onChange={(_, data) => {
                                                        setItem(data);
                                                        return onChange(data);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField {...params} {...field} fullWidth inputRef={ref} variant="outlined" label="Item" sx={{ fontSize: '24px', fontWeight: '900' }}></TextField>
                                                    )}
                                                />
                                            )}
                                        />
                                    </Item>
                                </Grid>
                                <Grid item xs={12} marginX={2} marginY={0}>
                                    {getValues("ItemDetail")?.material != undefined &&
                                        <FormLabel sx={{ fontSize: "20px", fontWeight: "700", color: "#191919" }}>{`Material: ${getValues("ItemDetail")?.material} - Description: ${getValues("ItemDetail")?.description} - Tray Size: ${getValues("ItemDetail")?.size}`}</FormLabel>}
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="species" label="Species" {...register("Species")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="variety" label="Variety" {...register("Variety")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="color" label="Color" {...register("Color")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="description" label="Description"  {...register("Description")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}>
                                        <TextField required type="number" variant="outlined" fullWidth id="croptime" label="Crop Time (Weeks)" {...register("CropTime", { valueAsNumber: true })} />
                                    </Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}>
                                        <Controller
                                            name="IsTransferType"
                                            control={control}
                                            defaultValue={false}
                                            render={({ field: { value, ref, ...field } }) => (
                                                <FormControlLabel
                                                    label="Transfer Type"
                                                    control={
                                                        <Checkbox
                                                            {...field}
                                                            inputRef={ref}
                                                            checked={!!value}
                                                            color="primary"
                                                            size={"medium"}
                                                            disableRipple
                                                        />
                                                    }
                                                />
                                            )}
                                        />
                                    </Item>
                                </Grid>
                                <Grid item xs={12}><Divider /></Grid>
                                <Grid item xs={4} >
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="customername-ordernumber" label="Customer Name / Order #" {...register("CustomerName")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="quantity" label="Quantity" {...register("Quantity")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="buffer" label="Buffer" {...register("Buffer")} /></Item>
                                </Grid>
                                <Grid item xs={4} >
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="startingbarcode" label="Starting Barcode" {...register("StartingBarcode")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}>
                                        <Controller
                                            {...register("SowDate", { valueAsDate: true })}
                                            control={control}
                                            render={
                                                ({ field: { onChange, ...restField } }) =>
                                                    <DatePicker
                                                        label="Sow Date"
                                                        onChange={(event) => { onChange(event); SetSowWeek() }}
                                                        renderInput={(params) =>
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                {...params}
                                                            />}
                                                        {...restField}
                                                    />
                                            }
                                        />
                                    </Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="sowweek" label="Sow Week" {...register("SowWeek")} /></Item>
                                </Grid>
                                <Grid item xs={4} >
                                <Controller
                                            {...register("ProductionDate", { valueAsDate: true })}
                                            control={control}
                                            render={
                                                ({ field: { onChange, ...restField } }) =>
                                                    <DatePicker
                                                        label="Production Date"
                                                        onChange={(event) => { onChange(event); SetProductionWeek() }}
                                                        renderInput={(params) =>
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                {...params}
                                                            />}
                                                        {...restField}
                                                    />
                                            }
                                        />
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="productionweek" label="Production Week" {...register("ProductionWeek")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="enteredby" label="Entered By" {...register("EnteredBy")} /></Item>
                                </Grid>
                                <Grid item xs={4} >
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="ap" label="AP" {...register("AP")} /></Item>
                                </Grid>
                                <Grid item xs={4}>
                                    <Item sx={{ bgcolor: 'transparent' }}><TextField required variant="outlined" fullWidth id="transfertraytime" label="Transfer Tray Time" {...register("TransferTrayTime")} /></Item>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={7}>
                    <Grid container spacing={0} padding={2} bgcolor='rgba(255, 255, 255, 1)' minHeight='calc(100vh - 60px)'>
                        <Grid item xs={2}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <Autocomplete
                                    value={currentGreenhouse}
                                    options={greenHouses}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, data) => {
                                        setCurrentGreenhouse(data!);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth variant="outlined" label="Greenhouses"></TextField>
                                    )}
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={2}  >
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <Autocomplete
                                    value={currentBay}
                                    options={bays}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, data) => {
                                        setCurrentBay(data!)
                                        setZones(data?.zones!)
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth variant="outlined" label="Bays"></TextField>
                                    )}
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={2}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <Autocomplete
                                    value={currentZone}
                                    options={zones}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, data) => {
                                        setCurrentZone(data!);
                                        setBenches(data?.benches!)
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth variant="outlined" label="Zones"></TextField>
                                    )}
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={2}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <Autocomplete
                                    value={currentBench}
                                    options={benches}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(_, data) => {
                                        setCurrentBench(data!);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth variant="outlined" label="Benches"></TextField>
                                    )}
                                />
                            </Item>
                        </Grid>
                        <Grid item xs={4}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="secondary"
                                    sx={{ height: '60px' }}
                                    onClick={() => { SimulateTrays(); handleClickOpen() }}
                                    endIcon={<ArrowRightIcon />}
                                >
                                    <Typography variant="h6" color="#FFF" fontWeight={700}>
                                        Add
                                    </Typography>
                                </Button>
                            </Item>
                        </Grid>
                        <Grid item xs={12}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <div style={{ height: 'calc(100vh - 197px)', width: '100%' }}>
                                    <StripedDataGrid
                                        rows={rows}
                                        columns={columns}
                                        pageSize={20}
                                        rowsPerPageOptions={[20]}
                                        checkboxSelection
                                        sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                                        getRowClassName={(params) =>
                                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                        }
                                    />
                                </div>
                            </Item>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <div>
                <Snackbar open={openSuccess} autoHideDuration={6000} onClose={handleSuccessClose}>
                    <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%', color: "#FFF" }}>
                        Trays Successfully Added!
                    </Alert>
                </Snackbar>
                <Alert severity="success"><Typography color="#FFF">Trays Successfully Added!</Typography></Alert>
            </div>

            <Dialog open={open} onClose={handleClose} maxWidth='xl' fullWidth>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h5" fontWeight={900}>{`Material: ${getValues("ItemDetail")?.material} - Description: ${getValues("ItemDetail")?.description} - Tray Size: ${getValues("ItemDetail")?.size}`}</Typography>
                        </Grid>
                        <Grid item xs={12}><Divider /></Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Species</Typography>
                            <Typography variant="body1">{getValues("Species")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Variety</Typography>
                            <Typography variant="body1">{getValues("Variety")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Color</Typography>
                            <Typography variant="body1">{getValues("Color")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Description</Typography>
                            <Typography variant="body1">{getValues("Description")}</Typography>
                        </Grid>
                        <Divider light />
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Crop Time</Typography>
                            <Typography variant="body1">{getValues("CropTime")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Is Transfer Type</Typography>
                            <Typography variant="body1">{getValues("IsTransferType")}</Typography>
                        </Grid>
                        <Grid item xs={12}><Divider /></Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Customer/Order #</Typography>
                            <Typography variant="body1">{getValues("CustomerName")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Quantity</Typography>
                            <Typography variant="body1">{getValues("Quantity")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Buffer</Typography>
                            <Typography variant="body1">{getValues("Buffer")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Starting Barcode</Typography>
                            <Typography variant="body1">{getValues("StartingBarcode")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Sow Date</Typography>
                            <Typography variant="body1">{getValues("SowDate")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Sow Week</Typography>
                            <Typography variant="body1">{getValues("SowWeek")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Production Date</Typography>
                            <Typography variant="body1">{getValues("ProductionDate")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Production Week</Typography>
                            <Typography variant="body1">{getValues("ProductionWeek")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Entered By</Typography>
                            <Typography variant="body1">{getValues("EnteredBy")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>AP</Typography>
                            <Typography variant="body1">{getValues("AP")}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="button" fontWeight={300}>Transfer Tray Time</Typography>
                            <Typography variant="body1">{getValues("TransferTrayTime")}</Typography>
                        </Grid>
                        <Grid item xs={12}><Divider /></Grid>
                        <Grid item xs={3}>
                            <Typography variant="button" fontWeight={600}>Greenhouse</Typography>
                            <Typography variant="body1">{currentGreenhouse?.name}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="button" fontWeight={600}>Bay</Typography>
                            <Typography variant="body1">{currentBay?.name}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="button" fontWeight={600}>Zone</Typography>
                            <Typography variant="body1">{currentZone?.name}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="button" fontWeight={600}>Bench</Typography>
                            <Typography variant="body1">{currentBench?.name}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Item sx={{ bgcolor: 'transparent' }}>
                                <div style={{ height: '45vh', width: '100%' }}>
                                    <StripedDataGrid
                                        rows={trayRows}
                                        columns={trayColumns}
                                        pageSize={10}
                                        rowsPerPageOptions={[10]}
                                        sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                                        getRowClassName={(params) =>
                                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                        }
                                    />
                                </div>
                            </Item>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose} size="large">Cancel</Button>
                    <Button color="secondary" size="large" variant="contained" type="submit" onClick={handleSubmit(onSubmit)}><Typography color="#FFF">Add Trays</Typography></Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}